import React, {useState, useRef, useEffect} from "react";
import styled from "styled-components";
import FileDropArea from "./FileDropArea";
import get from "lodash/get";
import "@szhsin/react-menu/dist/index.css";
import * as ReactDOM from "react-dom";
import "react-html5-camera-photo/build/css/index.css";
import {CloseIcon} from "../../../../common/components/elements/button/CloseButton";
import {hasWebCam, isMobile} from "src/core/common/utils";
import dynamic from "next/dynamic";
import {logModuleLoadError} from "src/core/common/utils";
import useRouter from "src/core/common/hooks/useRouter";
import routes from "src/core/common/routes";

const Camera = dynamic(() =>
  import("react-html5-camera-photo").catch(
    logModuleLoadError("react-html5-camera-photo", {critical: true})
  )
);

function FileUploadWrapper({onChange, hasVerificationService, LabelComponent, ...props}) {
  const inputRef = useRef(null);
  const router = useRouter();
  const [container, setContainer] = useState();
  const [useCamera, setUseCamera] = useState(false);
  const [hasCamera, setHasCamera] = useState(false);

  useEffect(() => {
    setContainer(document.getElementById("camera"));
  }, [setContainer]);

  function openFileDialog() {
    if (inputRef.current) {
      inputRef.current.click();
    }
  }

  function handleTakePhoto(dataUri) {
    onChange({dataUri});
    setUseCamera(false);
  }

  useEffect(() => {
    let mounted = true;
    hasWebCam((has) => {
      if (!mounted) return;
      setHasCamera(has);
    });
    return () => {
      mounted = false;
    };
  }, [setHasCamera]);

  if (!hasVerificationService && (isMobile() || !hasCamera)) {
    return (
      <FileUpload
        {...props}
        onChange={onChange}
        inputRef={inputRef}
        LabelComponent={LabelComponent}
        onClick={openFileDialog}
      />
    );
  }

  const openCamera = () => setUseCamera(true);

  const openIdVerification = () => {
    router.push(routes.identityVerification);
  };

  return (
    <>
      {container &&
        ReactDOM.createPortal(
          <>
            {useCamera && (
              <CameraContainer>
                <CloseButton onClick={() => setUseCamera(false)} />
                <Camera
                  onCameraError={(error) => {
                    console.log(error);
                    setUseCamera(false);
                    setHasCamera(false);
                  }}
                  onTakePhotoAnimationDone={(dataUri) => {
                    handleTakePhoto(dataUri);
                  }}
                />
              </CameraContainer>
            )}
          </>,
          container
        )}
      <Menu
        inputRef={inputRef}
        onChangeFileUpload={onChange}
        fileUploadProps={props}
        openFileDialog={openFileDialog}
        openCamera={openCamera}
        openIdVerification={openIdVerification}
        hasVerificationService={hasVerificationService}
        LabelComponent={LabelComponent}
      />
    </>
  );
}

function Menu({
  inputRef,
  onChangeFileUpload,
  fileUploadProps,
  openFileDialog,
  openCamera,
  openIdVerification,
  hasVerificationService,
  LabelComponent,
}) {
  const [ReactMenu, setReactMenu] = useState(null);
  useEffect(() => {
    let mounted = true;
    import("@szhsin/react-menu").then((mod) => {
      if (mounted) {
        setReactMenu(mod);
      }
    });
    return () => {
      mounted = false;
    };
  }, [setReactMenu]);

  if (!ReactMenu) return null;

  if (hasVerificationService) {
    return (
      <ReactMenu.Menu
        menuButton={
          <div>
            <FileUpload
              {...fileUploadProps}
              onChange={onChangeFileUpload}
              inputRef={inputRef}
              LabelComponent={LabelComponent}
            />
          </div>
        }
      >
        <ReactMenu.MenuItem onClick={openIdVerification}>Verify</ReactMenu.MenuItem>;
      </ReactMenu.Menu>
    );
  }

  return (
    <ReactMenu.Menu
      menuButton={
        <div>
          <FileUpload
            {...fileUploadProps}
            onChange={onChangeFileUpload}
            LabelComponent={LabelComponent}
            inputRef={inputRef}
          />
        </div>
      }
    >
      <ReactMenu.MenuItem onClick={openFileDialog}>Select File</ReactMenu.MenuItem>
      <ReactMenu.MenuItem onClick={openCamera}>Use Camera</ReactMenu.MenuItem>
    </ReactMenu.Menu>
  );
}

function FileUpload({
  label,
  Placeholder,
  description,
  className,
  selectedValue,
  hasErrors,
  fileValidator,
  image,
  initialImage,
  inputRef,
  onClick,
  LabelComponent,
  ...props
}) {
  const [dragging, setDragging] = useState(false);
  const fileName = get(selectedValue, "0.name", "").slice(0, 30);

  const EmptyImagePlaceholder = () => (
    <>
      {Placeholder && <Placeholder />}
      <Label hasErrors={hasErrors}>{fileName || label}</Label>
      <Description>{description}</Description>
    </>
  );

  function onFileDropped(files) {
    const isValid = files && files.length && imageFileValidator.test(files[0]);
    if (isValid) {
      props.onChange({
        target: {
          files,
        },
      });
    }
  }
  function onDragOver() {
    if (!dragging) {
      setDragging(true);
    }
  }
  function onDragEnd() {
    if (dragging) {
      setDragging(false);
    }
  }

  const imageToShow = () => {
    if (image) {
      return image;
    } else if (initialImage) {
      return initialImage;
    } else {
      return null;
    }
  };

  return (
    <FullWidthDropArea
      className={className}
      onFileDropped={onFileDropped}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
    >
      <FileUploadContainer hasErrors={hasErrors} onClick={onClick}>
        <Outline dragging={dragging}>
          {imageToShow() ? (
            <ImageContainer>
              <Image src={imageToShow()} alt={label} />
              <Tooltip>
                <EmptyImagePlaceholder />
              </Tooltip>
            </ImageContainer>
          ) : (
            <EmptyImagePlaceholder />
          )}
          <HiddenLabel htmlFor={`file-input-${label}`}>{label}</HiddenLabel>
          <HiddenFileInput
            id={`file-input-${label}`}
            ref={inputRef}
            type="file"
            accept={fileValidator.accept}
            {...props}
          />
        </Outline>
        {LabelComponent && <LabelComponent />}
      </FileUploadContainer>
    </FullWidthDropArea>
  );
}

const FullWidthDropArea = styled(FileDropArea)`
  width: 100%;
  box-sizing: border-box;
`;
const FileUploadContainer = styled.div`
  cursor: pointer;
  background: #fafafa;
  border: ${({hasErrors}) => (hasErrors ? "1px solid red" : "1px solid #ebebeb")};
  box-sizing: border-box;
  border-radius: ${({theme}) => theme.v2.decoration.border.radius.default};
  height: 150px;
  width: 100%;
  overflow: hidden;
  padding-bottom: ${({theme}) => theme.v2.spacing(7)};
  padding: ${({theme, isCentered}) => isCentered && theme.v2.spacing(4)};
`;
const Outline = styled.div`
  border: ${({dragging}) => (dragging ? "1px dashed #ebebeb" : 0)};
  background-color: ${({dragging}) => (dragging ? "white" : "transparent")};

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
`;
const Label = styled.p`
  text-align: center;
  font-size: ${({theme}) => theme.v2.typography.sizing.m.lg};
  margin: 8px 0 0 0;
  color: ${({hasErrors, theme}) => (hasErrors ? "red" : theme.v2.color.base.grey["900"])};
`;
const Description = styled.p`
  text-align: center;
  font-size: ${({theme}) => theme.v2.typography.sizing.xxs.lg};
  color: #8d8d8d;
  margin-bottom: 0;
`;
const HiddenFileInput = styled.input`
  position: fixed;
  top: -100em;
  opacity: 0;
`;
const ImageContainer = styled.div`
  display: block;
  width: 100%;
  height: auto;
  position: relative;
  padding: 56.25% 0 0 0;
  &:hover > img {
    opacity: 0.2;
  }
  &:hover div {
    visibility: visible;
  }
`;
const Image = styled.img`
  display: block;
  max-width: 100%;
  max-height: 100%;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  margin: auto;
`;
const Tooltip = styled.div`
  visibility: hidden;
  width: 100%;
  height: 100%;

  /* Position the tooltip */
  display: flex;
  flex-direction: column;
  position: absolute;
  z-index: 1;
  bottom: 0;
  margin: auto;
  justify-content: center;
  align-items: center;
`;

const CloseButton = styled(CloseIcon)`
  cursor: pointer;
  color: #fff;
`;

const CameraContainer = styled.div`
  width: 100vw;
  height: 100vh;
  background-color: black;
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  top: 0;
  left: 0;
  ${CloseButton} {
    position: absolute;
    top: ${({theme}) => theme.v2.spacing(8)};
    right: ${({theme}) => theme.v2.spacing(8)};
  }
`;

const HiddenLabel = styled.label`
  height: 0;
  color: transparent;
  pointer-events: none;
`;

export const imageFileValidator = {
  accept: ".jpg, .png, .jpeg, .gif, .bmp, .tif, .tiff|image/*;capture=camera",
  test: (file) => {
    return file.name.match(/.(jpg|jpeg|png|gif)$/i);
  },
};

export default FileUploadWrapper;
